import { z } from "zod";
import {
  AppointmentStatus,
  AppointmentPaymentStatus,
  AppointmentChargeStatus,
  ApprovalFeeOutcome,
  VisitType,
} from "@/api/enums";
import type { Endpoint } from "./types";

// ── Zod Schemas ─────────────────────────────────────────────────────

/**
 * What cancelling this appointment right now would do to the $50 approval fee,
 * as the API computes it.
 *
 * The notice window, the fee amount and whether cancelling is still allowed all
 * come from here rather than being recomputed client-side: the API is what
 * actually moves the money, and a local copy of the rule starts lying to
 * patients the moment the policy changes.
 *
 * Evaluated against the server clock at request time, so it is only true for
 * that response. An appointment can cross the window while a tab sits open.
 */
export const cancellationPolicySchema = z.object({
  noticeHours: z.number(),
  isWithinNoticeWindow: z.boolean(),
  canCancel: z.boolean(),
  refundAmountCents: z.number(),
  retainedAmountCents: z.number(),
});

/** What happened to the approval fee, and the paper trail for it. */
const refundStateShape = {
  approvalFeeOutcome: z.enum(ApprovalFeeOutcome),
  approvalRefundedCents: z.number(),
  approvalRefundedOnUtc: z.string().nullable(),
  refundId: z.string().nullable(),
  refundFailureReason: z.string().nullable(),
};

export const appointmentDashboardResponseSchema = z.object({
  id: z.string(),
  specialistId: z.string(),
  specialistName: z.string(),
  practiceName: z.string(),
  userId: z.string(),
  userName: z.string(),
  requestedTime: z.string().nullable(),
  startTime: z.string(),
  status: z.enum(AppointmentStatus),
  type: z.enum(VisitType),
  paymentStatus: z.enum(AppointmentPaymentStatus),
  approvalPaymentStatus: z.enum(AppointmentChargeStatus),
  completionPaymentStatus: z.enum(AppointmentChargeStatus),
  approvalChargedOnUtc: z.string().nullable(),
  completionChargedOnUtc: z.string().nullable(),
  ...refundStateShape,
  cancellationPolicy: cancellationPolicySchema,
  durationInMinutes: z.number(),
});

export const appointmentResponseSchema = z.object({
  id: z.string(),
  specialistId: z.string(),
  specialistSlug: z.string(),
  specialistName: z.string(),
  userName: z.string(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  userEmail: z.string(),
  userPhoneNumber: z.string(),
  dateOfBirth: z.string().nullable().optional(),
  isMedicare: z.boolean(),
  symptoms: z.string(),
  startTime: z.string(),
  requestedTime: z.string().nullable().optional(),
  status: z.enum(AppointmentStatus),
  visitType: z.enum(VisitType),
  paymentStatus: z.enum(AppointmentPaymentStatus),
  approvalPaymentStatus: z.enum(AppointmentChargeStatus),
  completionPaymentStatus: z.enum(AppointmentChargeStatus),
  approvalChargedOnUtc: z.string().nullable(),
  completionChargedOnUtc: z.string().nullable(),
  ...refundStateShape,
  cancellationPolicy: cancellationPolicySchema,
  durationInMinutes: z.number(),
  practiceName: z.string(),
  locationId: z.string().nullable().optional(),
  locationName: z.string().nullable().optional(),
  locationAddress: z.string().nullable().optional(),
  locationAddress2: z.string().nullable().optional(),
  locationCity: z.string().nullable().optional(),
  locationState: z.string().nullable().optional(),
  locationZip: z.string().nullable().optional(),
  searchedZipCode: z.string().nullable().optional(),
  emailMarketingConsent: z.boolean().optional(),
  smsConsent: z.boolean().optional(),
});

export const appointmentsPostRequestSchema = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  userEmail: z.email(),
  userPhoneNumber: z.string().min(10),
  specialistId: z.string(),
  locationId: z.string().optional(),
  // Id of the availability slot the patient picked. The API re-derives the
  // visit type from that slot (has a location → in-person, none →
  // telemedicine) and rejects the booking when it disagrees with `visitType`,
  // so both must be taken from the same slot object.
  timeSlotId: z.uuid(),
  appointmentDate: z.string(),
  visitType: z.enum(VisitType),
  dateOfBirth: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date of birth must be in YYYY-MM-DD format"),
  symptoms: z.string().min(1),
  searchedZipCode: z
    .string()
    .regex(/^\d{5}$/)
    .optional(),
  // Answered once at signup and stored on the patient. The API snapshots that
  // recorded answer when the field is absent, so the booking form omits it.
  emailMarketingConsent: z.boolean().optional(),
  smsConsent: z.boolean(),
});

export const appointmentProposeNewDoctorRequestSchema =
  appointmentsPostRequestSchema.extend({
    currentAppointmentId: z.string(),
    // Admins reassign a patient without necessarily picking from published
    // availability, so the API keeps the slot id optional on this route.
    timeSlotId: z.uuid().optional(),
  });

export const appointmentsStatusRequestSchema = z.object({
  appointmentId: z.string(),
});

export const appointmentsRescheduleRequestSchema = z.object({
  appointmentId: z.string(),
  newDate: z.string(),
});

export const checkPaymentStatusResponseSchema = z.object({
  setupIntentStatus: z.string().nullable(),
  paymentIntentStatus: z.string().nullable(),
  hasPaymentMethod: z.string().nullable(),
});

// ── Inferred Types ──────────────────────────────────────────────────

export type AppointmentsRequest = {
  fromDate?: string;
  toDate?: string;
  status?: AppointmentStatus;
  type?: VisitType;
  paymentStatus?: AppointmentPaymentStatus;
  /** Backs the refund-owed queue: pass ApprovalFeeOutcome.RefundOwed. */
  approvalFeeOutcome?: ApprovalFeeOutcome;
  take?: number;
  skip?: number;
};

export type CancellationPolicyPreview = z.infer<
  typeof cancellationPolicySchema
>;
export type AppointmentDashboardResponse = z.infer<
  typeof appointmentDashboardResponseSchema
>;
export type AppointmentResponse = z.infer<typeof appointmentResponseSchema>;
export type AppointmentsPostRequest = z.infer<
  typeof appointmentsPostRequestSchema
>;
export type AppointmentProposeNewDoctorRequest = z.infer<
  typeof appointmentProposeNewDoctorRequestSchema
>;
export type AppointmentsStatusRequest = z.infer<
  typeof appointmentsStatusRequestSchema
>;
export type AppointmentsRescheduleRequest = z.infer<
  typeof appointmentsRescheduleRequestSchema
>;
export type CheckPaymentStatusResponse = z.infer<
  typeof checkPaymentStatusResponseSchema
>;

// ── Endpoints ──────────────────────────────────────────────────────

export const appointmentEndpoints = {
  list: { method: "GET", path: "/appointments", auth: true } as Endpoint,

  getById: (id: string): Endpoint => ({
    method: "GET",
    path: `/appointments/${id}`,
    auth: true,
  }),

  create: { method: "POST", path: "/appointments", auth: true } as Endpoint,

  reschedule: {
    method: "PATCH",
    path: "/appointments/reschedule",
    auth: true,
  } as Endpoint,

  cancelByCustomer: {
    method: "PATCH",
    path: "/appointments/cancel-by-customer",
    auth: true,
  } as Endpoint,

  cancelByAdmin: {
    method: "PATCH",
    path: "/appointments/cancel-by-admin",
    auth: true,
  } as Endpoint,

  sendConfirmationLink: {
    method: "PATCH",
    path: "/appointments/send-confirmation-link",
    auth: true,
  } as Endpoint,

  approve: {
    method: "PATCH",
    path: "/appointments/approve",
    auth: true,
  } as Endpoint,

  confirmAndCharge: {
    method: "PATCH",
    path: "/appointments/confirm-and-charge",
    auth: true,
  } as Endpoint,

  complete: {
    method: "PATCH",
    path: "/appointments/complete",
    auth: true,
  } as Endpoint,

  noShow: {
    method: "PATCH",
    path: "/appointments/no-show",
    auth: true,
  } as Endpoint,

  markAsPaid: {
    method: "PATCH",
    path: "/appointments/mark-as-paid",
    auth: true,
  } as Endpoint,

  proposeNewDoctor: {
    method: "POST",
    path: "/appointments/propose-new-doctor",
    auth: true,
  } as Endpoint,

  checkPaymentStatus: (id: string): Endpoint => ({
    method: "GET",
    path: `/appointments/check-payment-status/${id}`,
    auth: true,
  }),

  generatePaymentLink: {
    method: "POST",
    path: "/appointments/generate-payment-link",
    auth: true,
  } as Endpoint,
} as const;
